import React, { useCallback, useEffect, useState } from "react";
import { View } from "react-native";
import { useTranslation } from "react-i18next";
import {
    useAuth,
    usePagination,
    useThemedStyle,
    DataTable,
    Button,
    Alert,
    Loading,
    Icon,
    useMerchantConfig,
    SearchInput,
    Surface,
    Spacer,
    useConfirm,
} from "@venuepos/react-common";
import type { StyleFunction } from "@venuepos/react-common";
import { useCustomerDeleteMutation, useCustomersQuery } from "graphql-sdk";
import type { GQCustomersQuery } from "graphql-sdk";
import { CustomerItem } from "./customer-item";
import { useAdminSession } from "../../../session";
import type { AvailableLocale } from "locales";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import { RootStackParamList } from "../../../navigation";
import { useHandleMutationError } from "../../../hooks";

export function CustomersTab() {
    const auth = useAuth();
    auth.enforce("merchant.customer");

    const { navigate } = useNavigation();
    const route = useRoute<RouteProp<RootStackParamList, "CUSTOMERS">>();
    const confirm = useConfirm();
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "name",
    });

    const [search, setSearch] = useState<string>("");
    const { currency } = useMerchantConfig();
    const [{ locale }] = useAdminSession(["locale"]);

    // GraphQL
    const { data, error, loading, refetch } = useCustomersQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
            search: {
                query: search,
            },
        },
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (route.params?.refetch) {
            refetch();
        }
    }, [refetch, route.params?.refetch]);

    // Actions

    const handleEdit = useCallback(
        (id: string) => {
            navigate("CUSTOMER_EDIT", {
                id,
            });
        },
        [navigate]
    );

    const handleSearchTextChange = useCallback(
        text => {
            setSearch(text);
            // return to first page in list
            onPageChange(0);
        },
        [onPageChange]
    );

    const [deleteCustomer] = useCustomerDeleteMutation();
    const { handleMutationError } = useHandleMutationError();

    const handleDelete = useCallback(
        async (customerId: string) => {
            if (
                !(await confirm(
                    t("backoffice.customer.delete", "Delete Customer?"),
                    t(
                        "backoffice.customer.delete_explain",
                        "If you choose to delete this customer, you will not be able to use related accounts, tags, etc. \n\nAre you sure, you want to delete?"
                    )
                ))
            ) {
                return;
            }

            await handleMutationError(
                async () =>
                    await deleteCustomer({ variables: { id: customerId! } }),
                t("common.deleted", "Deleted"),
                () => {
                    navigate("ACCOUNT_PAYMENTS", { refetch: true });
                }
            );
        },
        [confirm, deleteCustomer, handleMutationError, navigate, t]
    );

    return (
        <>
            <View style={styles.container}>
                <View style={styles.actionButtonContainer}>
                    <Button
                        style={styles.button}
                        type="secondary"
                        onPress={() => {
                            navigate("CUSTOMER_CREATE");
                        }}
                        testID="customerAccount:create"
                    >
                        {t("common.create", "Create")}
                    </Button>
                </View>
            </View>
            <SearchInput
                onChange={handleSearchTextChange}
                testID="customerSearch"
            />
            <Surface>
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title
                            sortDirection={sortBy === "name" && sortDirection}
                            onPress={() => onSortChange("name")}
                        >
                            {t("common.name", "Name")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "group.name" && sortDirection
                            }
                            onPress={() => onSortChange("group.name")}
                        >
                            {t(
                                "backoffice.common.customer_group",
                                "Customer group"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "createdAt" && sortDirection
                            }
                            onPress={() => onSortChange("createdAt")}
                        >
                            {t("common.created_at", "Created at")}
                        </DataTable.Title>
                        <DataTable.Title numeric>
                            {t("common.balance", "Balance")}
                        </DataTable.Title>
                        <DataTable.Title numeric style={styles.iconColumn}>
                            <Icon name="sort" color={styles.iconColumn.color} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {error ? (
                        <Alert type="error">
                            {t(
                                "common.error",
                                "There was an error: {{errorText}}",
                                {
                                    errorText: error.message,
                                }
                            )}
                        </Alert>
                    ) : loading ? (
                        <>
                            <Spacer />
                            <Loading />
                            <Spacer />
                        </>
                    ) : (
                        data?.customers.data.map(
                            (
                                item: GQCustomersQuery["customers"]["data"][0]
                            ) => (
                                <CustomerItem
                                    key={item.id}
                                    item={item}
                                    onEdit={() => handleEdit(item.id)}
                                    currency={currency}
                                    locale={locale as AvailableLocale}
                                    onDelete={() => {
                                        handleDelete(item.id);
                                    }}
                                />
                            )
                        )
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.customers.pagination.pages}
                        itemCount={data?.customers.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        justifyContent: "space-between",
        marginBottom: theme.spacingScale * 2,
        flexWrap: "wrap",
    },
    actionButtonContainer: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
    button: {
        alignSelf: "flex-end",
        marginLeft: theme.spacingScale * 2,
    },
    iconColumn: {
        flexBasis: 100,
        flexGrow: 0,
        flexShrink: 0,
        color: theme.colors.black,
    },
});
